import datadogRum, { type DatadogRumOptions } from './datadogRum';
import getBrowserLogger from '@zg-rentals/logger-browser';
import type { Logger, LoggerOptions } from '@zg-rentals/logger-browser';
import type { Plugin } from '@zg-rentals/rp-bootstrap-base';
import webVitals from './webVitals';
import { Bootstrap, BootstrapBuilder } from '@zg-rentals/rp-bootstrap-base';
import { BrowserTracer } from '@zg-rentals/trace/client';
import { DragnetBrowser, dragnetReporter } from '@zg-rentals/dragnet-browser';
import { MarlinBrowser, marlinReporter } from '@zg-rentals/marlin-browser';
import { Monitor, type Reporter } from '@zg-rentals/monitor-base';
import { getCurrentEnvForClientSide, PRODUCTION } from '@zg-rentals/environment-utils/client';
import { isBrowserExtensionError, isBrowserExtensionRejection } from './isBrowserExtensionError';
import { isDev, PRODUCTION_ENV } from '@zg-rentals/environment-utils';
import { logError, setLogErrorBeforeSend } from '@zg-rentals/log-error';

export class BootstrapBrowserBuilder extends BootstrapBuilder {
  private loggerOptions: LoggerOptions = {};
  private initializeLogger = (logger: Logger) => logger;

  withLoggerOptions(loggerOptions: LoggerOptions, initializeLogger?: (logger: Logger) => Logger) {
    this.loggerOptions = loggerOptions;
    if (initializeLogger) {
      this.initializeLogger = initializeLogger;
    }
    return this;
  }

  withHostedAppLoggerOptions(hostedApp: string, level = 'warn') {
    return this.withLoggerOptions({ level, logPath: `/${hostedApp}/hostedApp/log?appName=${this.appName}` });
  }

  withRentalHostedAppLoggerOptions(hostedApp: string, level = 'warn') {
    return this.withLoggerOptions({ level, logPath: `/rental/${hostedApp}/hostedApp/log?appName=${this.appName}` });
  }

  private readonly plugins: Array<Plugin> = [webVitals];
  withPlugin(plugin: Plugin) {
    this.plugins.push(plugin);
    return this;
  }

  withDatadogPlugin(applicationId: string, clientToken: string, service = this.appName, options?: DatadogRumOptions) {
    if (typeof window !== 'undefined' && getCurrentEnvForClientSide() === PRODUCTION) {
      this.plugins.push(
        datadogRum(applicationId, clientToken, service, `${this.buildNumber}`, PRODUCTION_ENV, options),
      );
    }

    return this;
  }

  private onRegisterCallbacks: Array<() => void> = [];
  withOnRegister(callback: () => void) {
    this.onRegisterCallbacks.push(callback);
    return this;
  }

  build() {
    setLogErrorBeforeSend(this.logErrorBeforeSend);
    return new BootstrapBrowser(
      this.appName,
      this.buildNumber,
      this.plugins,
      { ...this.loggerOptions, initializeLogger: this.initializeLogger },
      [
        () => marlinReporter(new MarlinBrowser(this.appName), this.appName, 'browser'),

        (logger) =>
          dragnetReporter(
            new DragnetBrowser(this.appName, this.buildNumber),
            logger.child({ name: 'dragnet-browser-reporter' }),
          ),
      ],
      this.onRegisterCallbacks,
    );
  }
}

export class BootstrapBrowser extends Bootstrap<BrowserTracer> {
  constructor(
    appName: string,
    buildNumber: number,
    plugins: Array<Plugin>,
    loggerOptions: LoggerOptions & { initializeLogger: (logger: Logger) => Logger },
    reporters: Array<(logger: Logger) => Reporter>,
    onRegisterCallbacks: Array<() => void>,
  ) {
    const logger = loggerOptions.initializeLogger(
      getBrowserLogger({
        name: appName,
        ...loggerOptions,
        logPath: isDev() ? undefined : loggerOptions.logPath,
      }),
    );

    const monitor = new Monitor(reporters.map((reporter) => reporter(logger)));

    const tracer = new BrowserTracer();

    super(appName, buildNumber, plugins, logger, monitor, tracer);

    this.onRegister(() => {
      monitor.count({ name: 'browser init' });
      monitor.count({ name: `buildNumber browser ${this.buildNumber}` });

      tracer.init();

      window.addEventListener('error', ({ error, filename, lineno, colno }) => {
        if (error != null && !isBrowserExtensionError(filename)) {
          logError({
            error,
            context: {
              filename,
              lineNumber: lineno,
              columnNumber: colno,
            },
          });
        }
      });

      window.addEventListener('unhandledrejection', ({ reason }) => {
        if (!isBrowserExtensionRejection(reason)) {
          logError({
            error: reason instanceof Error ? reason : new Error(`${reason}`),
            errorType: 'unhandledRejection',
          });
        }
      });

      onRegisterCallbacks.forEach((callback) => callback());
    });
  }
}

export { Bootstrap };
